import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Camera, X, CheckCircle2, Wrench, Printer } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicApi = axios.create({ baseURL: API });

const localISO = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};
const todayISO = () => localISO(new Date());

const CHECKLIST_STATUS_OPTIONS = [
    { value: "bien", label: "Bien", cls: "bg-emerald-100 text-emerald-700" },
    { value: "regular", label: "Regular", cls: "bg-amber-100 text-amber-700" },
    { value: "mal", label: "Mal", cls: "bg-rose-100 text-rose-700" },
    { value: "na", label: "N/A", cls: "bg-slate-100 text-slate-600" },
];

export default function PublicChecklist() {
    const [searchParams] = useSearchParams();
    const preselectedCarId = searchParams.get("car_id") || "";
    const [cars, setCars] = useState([]);
    const [template, setTemplate] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitted, setSubmitted] = useState(false);

    const [submittedBy, setSubmittedBy] = useState("");
    const [carId, setCarId] = useState(preselectedCarId);
    const [date, setDate] = useState(todayISO());
    const [mileage, setMileage] = useState("");
    const [generalNotes, setGeneralNotes] = useState("");
    const [itemStates, setItemStates] = useState({});
    const [uploadingKey, setUploadingKey] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        Promise.all([publicApi.get("/public/cars"), publicApi.get("/public/checklist-template")])
            .then(([carsRes, tplRes]) => {
                setCars(carsRes.data);
                setTemplate(tplRes.data);
                if (preselectedCarId && carsRes.data.some((c) => c.id === preselectedCarId)) {
                    setCarId(preselectedCarId);
                }
            })
            .catch(() => toast.error("No se pudo cargar el formulario, intenta de nuevo"))
            .finally(() => setLoading(false));
    }, [preselectedCarId]);

    const setItemStatus = (key, status) => setItemStates((s) => ({ ...s, [key]: { ...s[key], status } }));
    const setItemNotes = (key, notes) => setItemStates((s) => ({ ...s, [key]: { ...s[key], notes } }));

    const handleItemPhotoUpload = async (key, fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;
        setUploadingKey(key);
        try {
            for (const file of files) {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await publicApi.post("/public/uploads/checklist-photo", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                setItemStates((s) => ({ ...s, [key]: { ...s[key], photos: [...(s[key]?.photos || []), data.path] } }));
            }
            toast.success(files.length > 1 ? "Fotos agregadas" : "Foto agregada");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al subir la foto");
        } finally {
            setUploadingKey(null);
        }
    };
    const removeItemPhoto = (key, path) =>
        setItemStates((s) => ({ ...s, [key]: { ...s[key], photos: (s[key]?.photos || []).filter((p) => p !== path) } }));

    const submit = async () => {
        if (!submittedBy.trim()) return toast.error("Escribe tu nombre");
        if (!carId) return toast.error("Selecciona un auto");
        if (!date) return toast.error("La fecha es obligatoria");
        if (mileage === "") return toast.error("El kilometraje es obligatorio");
        const missingPhotos = template.filter((t) => (itemStates[t.key]?.photos || []).length === 0);
        if (missingPhotos.length > 0) {
            const names = missingPhotos.slice(0, 3).map((t) => t.label).join(", ");
            const extra = missingPhotos.length > 3 ? ` y ${missingPhotos.length - 3} más` : "";
            return toast.error(`Falta al menos 1 foto en: ${names}${extra}`);
        }
        const payload = {
            car_id: carId,
            submitted_by: submittedBy.trim(),
            date,
            mileage: mileage !== "" ? Number(mileage) : null,
            general_notes: generalNotes || null,
            photos: [],
            items: template.map((t) => ({
                key: t.key,
                label: t.label,
                category: t.category,
                status: itemStates[t.key]?.status || "bien",
                notes: itemStates[t.key]?.notes || null,
                photos: itemStates[t.key]?.photos || [],
            })),
        };
        setSaving(true);
        try {
            await publicApi.post("/public/checklists", payload);
            setSubmitted(true);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo enviar la revisión, intenta de nuevo");
        } finally {
            setSaving(false);
        }
    };

    const resetForNew = () => {
        setSubmitted(false);
        setCarId(preselectedCarId || "");
        setMileage("");
        setGeneralNotes("");
        setItemStates({});
        setDate(todayISO());
    };

    if (submitted) {
        return (
            <div className="min-h-screen bg-slate-50 grid place-items-center p-6" data-testid="public-checklist-success">
                <div className="max-w-md text-center space-y-4">
                    <CheckCircle2 size={48} className="text-emerald-600 mx-auto" />
                    <h1 className="text-2xl font-display font-semibold text-slate-900">¡Revisión enviada!</h1>
                    <p className="text-slate-500 text-sm">Gracias, {submittedBy}. Tu revisión quedó registrada correctamente.</p>
                    <Button onClick={resetForNew} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="public-checklist-new-btn">
                        Registrar otra revisión
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="public-checklist-page">
            <div className="max-w-2xl mx-auto space-y-6">
                <header className="text-center">
                    <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Uber Tracking</p>
                    <h1 className="text-2xl md:text-3xl font-display font-semibold text-slate-900 mt-1">Revisión físico-mecánica</h1>
                    <p className="text-sm text-slate-500 mt-1">Llena este formulario después de revisar el auto. No necesitas cuenta ni contraseña.</p>
                    <a
                        href={`${API}/public/checklist-template/pdf`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 mt-2"
                        data-testid="public-checklist-print-blank-link"
                    >
                        <Printer size={13} /> Imprimir formato en blanco para llenar a mano
                    </a>
                </header>

                {loading ? (
                    <div className="text-center text-slate-400 text-sm py-10">Cargando…</div>
                ) : (
                    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs">Tu nombre *</Label>
                                <Input
                                    value={submittedBy}
                                    onChange={(e) => setSubmittedBy(e.target.value)}
                                    placeholder="Ej. Juan Pérez"
                                    data-testid="public-checklist-name-input"
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Auto *</Label>
                                <Select value={carId} onValueChange={setCarId}>
                                    <SelectTrigger data-testid="public-checklist-car-select">
                                        <SelectValue placeholder="Selecciona un auto" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {cars.map((c) => (
                                            <SelectItem key={c.id} value={c.id}>{c.plate} · {c.brand} {c.model}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs">Fecha *</Label>
                                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="public-checklist-date-input" />
                            </div>
                            <div>
                                <Label className="text-xs">Kilometraje *</Label>
                                <Input
                                    type="number"
                                    value={mileage}
                                    onChange={(e) => setMileage(e.target.value)}
                                    placeholder="Ej. 45000"
                                    data-testid="public-checklist-mileage-input"
                                />
                            </div>
                        </div>

                        <PublicChecklistGroup
                            title="Estado físico"
                            items={template.filter((t) => t.category === "fisico")}
                            states={itemStates}
                            setStatus={setItemStatus}
                            setNotes={setItemNotes}
                            onPhotoUpload={handleItemPhotoUpload}
                            onRemovePhoto={removeItemPhoto}
                            uploadingKey={uploadingKey}
                        />
                        <PublicChecklistGroup
                            title="Estado mecánico"
                            items={template.filter((t) => t.category === "mecanico")}
                            states={itemStates}
                            setStatus={setItemStatus}
                            setNotes={setItemNotes}
                            onPhotoUpload={handleItemPhotoUpload}
                            onRemovePhoto={removeItemPhoto}
                            uploadingKey={uploadingKey}
                        />

                        <div>
                            <Label className="text-xs">Notas generales</Label>
                            <Textarea
                                value={generalNotes}
                                onChange={(e) => setGeneralNotes(e.target.value)}
                                rows={2}
                                placeholder="Observaciones generales (opcional)"
                                data-testid="public-checklist-general-notes"
                            />
                        </div>

                        <Button
                            onClick={submit}
                            disabled={saving}
                            className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                            data-testid="public-checklist-submit-btn"
                        >
                            {saving ? "Enviando…" : "Enviar revisión"}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}

function PublicChecklistGroup({ title, items, states, setStatus, setNotes, onPhotoUpload, onRemovePhoto, uploadingKey }) {
    return (
        <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                <Wrench size={13} className="text-slate-400" /> {title}
            </div>
            <div className="space-y-2">
                {items.map((t) => {
                    const state = states[t.key] || { status: "bien", notes: "", photos: [] };
                    const photos = state.photos || [];
                    return (
                        <div key={t.key} className="p-3 rounded-md border border-slate-200 bg-slate-50/50" data-testid={`public-checklist-item-${t.key}`}>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                <div className="text-sm text-slate-800 font-medium">{t.label}</div>
                                <div className="flex gap-1">
                                    {CHECKLIST_STATUS_OPTIONS.map((opt) => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => setStatus(t.key, opt.value)}
                                            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                                                state.status === opt.value
                                                    ? `${opt.cls} border-transparent`
                                                    : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                                            }`}
                                            data-testid={`public-checklist-item-${t.key}-${opt.value}`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <Input
                                value={state.notes || ""}
                                onChange={(e) => setNotes(t.key, e.target.value)}
                                placeholder="Notas de este apartado (opcional)"
                                className="mt-2 text-xs h-8 bg-white"
                                data-testid={`public-checklist-item-${t.key}-notes`}
                            />
                            <div className="mt-2">
                                <Label className={`text-xs flex items-center gap-1 ${photos.length === 0 ? "text-rose-600" : "text-slate-500"}`}>
                                    <Camera size={12} /> Foto obligatoria {photos.length === 0 && "(falta)"}
                                </Label>
                                {photos.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-1.5">
                                        {photos.map((p) => (
                                            <div key={p} className="relative">
                                                <img src={`${API}/public/checklist-photos/${p}`} alt="" className="w-14 h-14 rounded object-cover border border-slate-200" />
                                                <button
                                                    type="button"
                                                    onClick={() => onRemovePhoto(t.key, p)}
                                                    className="absolute -top-1.5 -right-1.5 bg-slate-900 text-white rounded-full p-0.5"
                                                    data-testid={`public-checklist-item-${t.key}-remove-photo-${p}`}
                                                >
                                                    <X size={10} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={(e) => { onPhotoUpload(t.key, e.target.files); e.target.value = ""; }}
                                    disabled={uploadingKey === t.key}
                                    className="mt-1.5 text-xs w-full text-slate-600 file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-slate-900 file:text-white file:text-xs hover:file:bg-slate-800 disabled:opacity-50"
                                    data-testid={`public-checklist-item-${t.key}-photo-input`}
                                />
                                {uploadingKey === t.key && <p className="text-xs text-slate-500 mt-1">Subiendo…</p>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
